import { Alert, Snackbar } from '@mui/material';
import React, { createContext, useCallback, useContext, useMemo } from 'react';

import { ACH_REQUEST_ERROR_EVENT, AchRequestErrorDetail } from '../util/httpError';
import posthog from '../util/posthog';

type SnackBarContextActions = {
  showSnackBar: (text: string, color: Severity, tracking?: SnackBarTracking) => void;
};

type Severity = 'success' | 'info' | 'warning' | 'error';

type SnackBarTracking = {
  event: string;
  formOpenedAt?: number;
  [key: string]: unknown;
};

const SnackBarContext = createContext({} as SnackBarContextActions);

interface SnackBarProviderProps {
  children?: React.ReactNode;
}

function SnackBarProvider(props: SnackBarProviderProps) {
  const { children } = props;
  const [open, setOpen] = React.useState<boolean>(false);
  const [message, setMessage] = React.useState<string>('');
  const [severity, setSeverity] = React.useState<Severity>('info');

  const showSnackBar = useCallback((text: string, color: Severity, tracking?: SnackBarTracking) => {
    if (tracking) {
      const { event, formOpenedAt, ...properties } = tracking;
      if (formOpenedAt !== undefined) {
        properties.duration_seconds = Math.round((Date.now() - formOpenedAt) / 1000);
      }
      if (color === 'error') {
        posthog.captureException(new Error(`${event}: ${text}`), { properties });
      } else {
        posthog.capture(event, properties);
      }
    }
    setMessage(text);
    setSeverity(color);
    setOpen(true);
  }, []);

  // Surface ACH-backend request failures dispatched by the fetch chokepoint.
  // The writes-disabled 503 gets a purpose-built warning; any other ACH failure
  // gets an attributable error toast so a flipped read failing against ACH is
  // diagnosable during prod parity testing.
  React.useEffect(() => {
    const handler = (event: Event) => {
      const { detail } = event as CustomEvent<AchRequestErrorDetail>;
      if (!detail) return;
      if (detail.writesDisabled) {
        showSnackBar('Saving is temporarily disabled on the new backend — your change was not saved.', 'warning');
        return;
      }
      const reason = detail.problem?.detail || detail.problem?.title || detail.message || '';
      showSnackBar(`ACH backend request failed (${detail.status})${reason ? `: ${reason}` : ''}`, 'error');
    };
    window.addEventListener(ACH_REQUEST_ERROR_EVENT, handler);
    return () => window.removeEventListener(ACH_REQUEST_ERROR_EVENT, handler);
  }, [showSnackBar]);

  const handleClose = () => {
    setOpen(false);
  };

  const value = useMemo(
    () => ({
      showSnackBar,
    }),
    [showSnackBar]
  );

  return (
    <SnackBarContext.Provider value={value}>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={handleClose}
      >
        <Alert onClose={handleClose} severity={severity}>
          {' '}
          {message}
        </Alert>
      </Snackbar>
      {children}
    </SnackBarContext.Provider>
  );
}

const useSnackBar = (): SnackBarContextActions => {
  const context = useContext(SnackBarContext);

  if (!context) {
    throw new Error('useSnackBar must be used within an SnackBarProvider');
  }

  return context;
};

export { SnackBarProvider, useSnackBar };
